import React, { useRef, useState } from "react";
import { FileUp, Loader2, RefreshCw, BellRing } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const KIND = {
  updated: ["Atualizado", "text-emerald-700 bg-emerald-50"],
  created: ["Novo ASO", "text-sky-700 bg-sky-50"],
  unmatched: ["Colaborador não identificado", "text-amber-700 bg-amber-50"],
  failed: ["Falha na leitura", "text-rose-700 bg-rose-50"],
};

const ResultList = ({ job }) => (
  <div className="space-y-3" data-testid="aso-update-result">
    <p className="text-sm text-slate-600">
      {job.status === "done" ? "Concluído" : "Lendo ASOs com IA..."} — {job.processed}/{job.total}
    </p>
    <div className="h-2 overflow-hidden rounded-full bg-slate-100">
      <div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${(job.processed / job.total) * 100}%` }} />
    </div>
    <div className="max-h-64 space-y-1.5 overflow-y-auto">
      {(job.results || []).map((r, i) => (
        <div key={i} className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-2 text-sm" data-testid={`aso-update-row-${i}`}>
          <div className="min-w-0">
            <p className="truncate font-medium text-slate-800">{r.employee_name}</p>
            <p className="truncate text-xs text-slate-400">{r.file}{r.valid_until ? ` · validade ${r.valid_until}` : ""}</p>
          </div>
          <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${KIND[r.kind][1]}`}>{KIND[r.kind][0]}</span>
        </div>
      ))}
    </div>
  </div>
);

export function AsoAlertsButton() {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/medical-records/send-alerts");
      toast.success(data.sent_items ? `E-mail enviado com ${data.sent_items} ASO(s) vencidos e a vencer para ${data.recipients} destinatário(s)` : "Nenhum ASO vencido ou a vencer em 60 dias");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <Button variant="outline" onClick={send} disabled={busy} title="Envia agora um e-mail com todos os ASOs vencidos e a vencer (60 dias)" data-testid="aso-send-alerts-btn">
      {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <BellRing className="mr-1.5 h-4 w-4 text-amber-600" />} Avisar vencimentos
    </Button>
  );
}

export function AsoUpdate({ employeeId, employeeName, compact = false, onDone }) {
  const [job, setJob] = useState(null);
  const ref = useRef(null);
  const testid = compact ? `aso-update-row-btn-${employeeId}` : "aso-update-btn";

  const poll = async (id) => {
    const { data } = await api.get(`/ai-zip-status/${id}`);
    setJob(data);
    if (data.status !== "done") setTimeout(() => poll(id), 2500);
    else onDone?.();
  };

  const upload = async (files) => {
    if (!files?.length) return;
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    if (employeeId) fd.append("employee_id", employeeId);
    try {
      const { data } = await api.post("/medical-records/update-aso", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      setJob({ ...data, processed: 0, results: [], status: "processing" });
      poll(data.job_id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao enviar ASO");
    }
    if (ref.current) ref.current.value = "";
  };

  return (
    <>
      <input ref={ref} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.zip" multiple={!compact} className="hidden"
        onChange={(e) => upload(e.target.files)} data-testid={`${testid}-input`} />
      {compact ? (
        <Button variant="ghost" size="icon" className="h-8 w-8" title={`Subir ASO atualizado de ${employeeName}`} onClick={() => ref.current?.click()} data-testid={testid}>
          <RefreshCw className="h-4 w-4 text-emerald-600" />
        </Button>
      ) : (
        <Button variant="outline" onClick={() => ref.current?.click()} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={testid}>
          <FileUp className="mr-1.5 h-4 w-4" /> Atualizar ASO (PDF ou ZIP)
        </Button>
      )}
      <Dialog open={!!job} onOpenChange={(o) => !o && job?.status === "done" && setJob(null)}>
        <DialogContent data-testid="aso-update-dialog">
          <DialogHeader><DialogTitle>Atualização de ASO</DialogTitle></DialogHeader>
          {job && <ResultList job={job} />}
          <DialogFooter>
            <Button disabled={job?.status !== "done"} onClick={() => setJob(null)} data-testid="aso-update-close">
              {job?.status !== "done" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null} Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
